/* eslint-disable react-hooks/exhaustive-deps */
'use client';
import { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  TextField,
  InputAdornment,
  Typography
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { fleetApiGo } from '../../../../api/axiosClient';
import { useLanguage } from '../../../../utils/i18n';

const ViewAllServiceReminder = ({ serviceTypeName, statusFilter }) => {
  const { t } = useLanguage();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const handleFilterClientSide = (data, typeValue) => {
    return data.filter((res) => res.status_notif === typeValue || res.status_notif_dist_both === typeValue);
  };

  const fetchDetail = async () => {
    if (!serviceTypeName) return;
    setLoading(true);

    try {
      const val = statusFilter === 'Warning' ? 'Warning' : statusFilter === 'Danger' ? 'Danger' : 'Not-notif';
      const typeService = 'both';
      const val2 = typeService !== 'both' ? '' : val;
      let columns = [
        { name: 'license_plate', logic_operator: 'like', value: search, operator: 'AND' },
        { name: 'service_type_name', logic_operator: '=', value: serviceTypeName, operator: 'AND' }
      ];
      if (statusFilter !== 'all' && typeService !== 'both') {
        columns.push(
          { name: 'status_notif', logic_operator: '=', value: val, operator: 'AND' },
          { name: 'status_notif_distance', logic_operator: '=', value: val2, operator: 'AND' }
        );
      }
      const payload = {
        joins: [{ name: 'service_type', column_results: ['name'] }],
        orders: { columns: ['day_left'], ascending: true },
        paging: { start: 0, length: -1 },
        columns: columns
      };

      const resp = await fleetApiGo.post('/dashboard-work-order/search', payload);
      let finalData = resp?.data || [];
      if (finalData.length > 0 && val !== 'Not-notif' && typeService === 'both') {
        finalData = handleFilterClientSide(finalData, val);
      }
      setRows(finalData);
    } catch (err) {
      console.error('Fetch detail error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => fetchDetail(), 500);
    return () => clearTimeout(timer);
  }, [serviceTypeName, statusFilter, search]);

  const filteredRows = rows.filter((row) => row.license_plate?.toLowerCase().includes(search.toLowerCase()));

  const hasTimeColumn = filteredRows.some((row) => ['time', 'both', 'engine_work_time'].includes(row.schedule_by));
  const hasDistanceColumn = filteredRows.some((row) => ['distance', 'both'].includes(row.schedule_by));

  const activeColSpan = 3 + (hasTimeColumn ? 2 : 0) + (hasDistanceColumn ? 2 : 0);
  const fmtDateID = (iso) => {
    if (!iso) return '-';
    const d = new Date(iso);
    if (isNaN(d)) return '-';
    return d.toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric' });
  };
  return (
    <Box sx={{ p: 0 }}>
      <Paper sx={{ m: 1, p: 2 }} elevation={0}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <TextField
            placeholder={t('serviceReminder.searchVehicle', 'Search Vehicle')}
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }
            }}
          />
        </Box>
        <TableContainer sx={{ maxHeight: '70vh', borderRadius: 2, border: '1px solid #e2e8f0' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, backgroundColor: '#f8fafc', color: '#475569', borderBottom: '2px solid #e2e8f0' }}>
                  {t('serviceReminder.vehicle', 'Vehicle')}
                </TableCell>
                <TableCell sx={{ fontWeight: 700, backgroundColor: '#f8fafc', color: '#475569', borderBottom: '2px solid #e2e8f0' }}>
                  {t('serviceReminder.serviceType', 'Service Type')}
                </TableCell>
                {hasDistanceColumn && (
                  <TableCell sx={{ fontWeight: 700, backgroundColor: '#f8fafc', color: '#475569', borderBottom: '2px solid #e2e8f0' }}>
                    {t('serviceReminder.nextOdo', 'Next Odo')}
                  </TableCell>
                )}
                {hasTimeColumn && (
                  <TableCell sx={{ fontWeight: 700, backgroundColor: '#f8fafc', color: '#475569', borderBottom: '2px solid #e2e8f0' }}>
                    {t('serviceReminder.nextDate', 'Next Date')}
                  </TableCell>
                )}
                <TableCell sx={{ fontWeight: 700, backgroundColor: '#f8fafc', color: '#475569', borderBottom: '2px solid #e2e8f0' }}>
                  {t('serviceReminder.currentOdo', 'Current Odo')}
                </TableCell>
                {hasTimeColumn && (
                  <TableCell sx={{ fontWeight: 700, backgroundColor: '#f8fafc', color: '#475569', borderBottom: '2px solid #e2e8f0' }}>
                    {t('serviceReminder.daysReminder', 'Days Reminder')}
                  </TableCell>
                )}
                {hasDistanceColumn && (
                  <TableCell sx={{ fontWeight: 700, backgroundColor: '#f8fafc', color: '#475569', borderBottom: '2px solid #e2e8f0' }}>
                    {t('serviceReminder.odoReminder', 'Odo Reminder')}
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={activeColSpan} align="center" sx={{ py: 3 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : filteredRows.length > 0 ? (
                filteredRows.map((row, index) => {
                  const isDanger = row.status_notif === 'Danger';
                  const isWarning = row.status_notif === 'Warning';
                  const isTimeType = ['time', 'both', 'engine_work_time'].includes(row.schedule_by);
                  const isDistanceType = ['distance', 'both'].includes(row.schedule_by);

                  // Modern pastel colors for rows based on alert status
                  const rowBgColor = isDanger ? '#fef2f2' : isWarning ? '#fffbeb' : '#ffffff';
                  const rowHoverColor = isDanger ? '#fee2e2' : isWarning ? '#fef3c7' : '#f8fafc';

                  return (
                    <TableRow
                      key={index}
                      sx={{
                        backgroundColor: rowBgColor,
                        transition: 'background-color 0.2s',
                        '&:hover': {
                          backgroundColor: rowHoverColor
                        }
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          {/* Elegant visual indicator dot */}
                          {isDanger && <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#ef4444', flexShrink: 0 }} />}
                          {isWarning && <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#f59e0b', flexShrink: 0 }} />}
                          {!isDanger && !isWarning && (
                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'transparent', flexShrink: 0 }} />
                          )}
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155' }}>
                            {row.license_plate}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ color: '#475569' }}>{row.service_type_name}</TableCell>
                      {hasDistanceColumn && (
                        <TableCell sx={{ fontWeight: 600, color: '#334155' }}>
                          {isDistanceType ? row.next_service_odo?.toLocaleString() : '-'}
                        </TableCell>
                      )}
                      {hasTimeColumn && (
                        <TableCell sx={{ fontWeight: 600, color: '#334155' }}>
                          {isTimeType ? fmtDateID(row.next_service_date) : '-'}
                        </TableCell>
                      )}
                      <TableCell sx={{ color: '#475569' }}>{row.current_odometer?.toLocaleString()}</TableCell>
                      {hasTimeColumn && (
                        <TableCell sx={{ fontWeight: 700, color: isDanger ? '#ef4444' : isWarning ? '#f59e0b' : '#334155' }}>
                          {isTimeType ? row.day_left : '-'}
                        </TableCell>
                      )}
                      {hasDistanceColumn && (
                        <TableCell sx={{ fontWeight: 700, color: isDanger ? '#ef4444' : isWarning ? '#f59e0b' : '#334155' }}>
                          {isDistanceType ? row.rest_odo?.toLocaleString() : '-'}
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={activeColSpan} align="center" sx={{ py: 8 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', opacity: 0.8 }}>
                      <Box
                        sx={{
                          width: 64,
                          height: 64,
                          mb: 2,
                          bgcolor: '#f1f5f9',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <SearchIcon sx={{ fontSize: 32, color: '#94a3b8' }} />
                      </Box>
                      <Typography variant="body1" sx={{ fontWeight: 600, color: '#64748b' }}>
                        {t('serviceReminder.noDataFound', 'No data found')}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#94a3b8', mt: 0.5 }}>
                        {t('serviceReminder.tryAdjustingFilter', 'Try adjusting your search filter.')}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default ViewAllServiceReminder;
